// Public landing for prints.evrststudio.com. Content is editable via
// Admin → Settings → Evrst Printpage (writes to site_settings.prints_page);
// this page reads /api/site-content/prints_page on mount and merges the
// saved values over the hardcoded defaults in prints-page-content.ts.
import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '@workos-inc/authkit-react'
import { PRINTS_PAGE_DEFAULTS, PrintsPageContent, mergePrintsContent } from '../lib/prints-page-content'

export default function Home() {
  const nav = useNavigate()
  const [c, setC] = useState<PrintsPageContent>(PRINTS_PAGE_DEFAULTS)

  // Pull saved content from the admin editor. Silent failure falls back to defaults.
  useEffect(() => {
    fetch('/api/site-content/prints_page')
      .then((r) => r.json())
      .then((d) => { if (d?.value) setC(mergePrintsContent(d.value)) })
      .catch(() => { /* keep defaults */ })
  }, [])

  // If a returnTo was stashed by Login.tsx, honour it on the way through.
  useEffect(() => {
    let cancelled = false
    fetch('/api/me', { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => {
        if (cancelled || !d?.authenticated) return
        const returnTo = (() => { try { return sessionStorage.getItem('evrst-prints:returnTo') } catch { return null } })()
        if (returnTo) {
          try { sessionStorage.removeItem('evrst-prints:returnTo') } catch { /* ignore */ }
          nav(returnTo, { replace: true })
        }
      }).catch(() => { /* anon, stay on home */ })
    return () => { cancelled = true }
  }, [nav])

  return (
    <div className="min-h-screen flex flex-col bg-white text-ink-2">
      <header className="bg-white border-b border-border">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/" className="font-heading uppercase font-bold tracking-[0.22em] text-sm text-ink">EVRST<span className="text-gold">.</span>PRINTS</Link>
          <nav className="flex items-center gap-7 text-xs uppercase tracking-[0.18em] font-semibold text-ink-2">
            <a href="#path"      className="hover:text-ink">Services</a>
            <a href="#garments"  className="hover:text-ink">How it works</a>
            <a href="#faq"       className="hover:text-ink">FAQ</a>
            <HeaderAuthBox />
          </nav>
        </div>
      </header>

      {/* HERO */}
      <section className="bg-ink text-white">
        <div className="max-w-6xl mx-auto px-6 py-16 md:py-20 grid md:grid-cols-[1.1fr_1fr] gap-10 items-center">
          <div>
            {c.hero.eyebrow && <p className="text-white/55 text-[11px] tracking-[0.2em] uppercase mb-5">{c.hero.eyebrow}</p>}
            <h1 className="font-heading uppercase font-bold leading-[0.92] text-5xl md:text-6xl">
              {c.hero.titleLine1}
              <span className="italic text-gold block">{c.hero.titleLine2}</span>
            </h1>
            <p className="mt-6 text-white/70 max-w-md leading-relaxed">{c.hero.body}</p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/designer" className="btn-gold">{c.hero.primaryCtaLabel}</Link>
              <Link to="/dtf" className="inline-flex items-center justify-center gap-2 px-5 py-2.5 border border-white/40 text-white font-semibold uppercase tracking-wide rounded text-sm hover:bg-white hover:text-ink transition-colors">{c.hero.secondaryCtaLabel}</Link>
            </div>
          </div>
          <div className="hidden md:block">
            <div className="rounded-lg bg-white shadow-2xl overflow-hidden border border-white/10">
              <div className="h-7 bg-[#E5E7EB] flex items-center px-3 gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-[#F87171]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#FBBF24]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#34D399]" />
              </div>
              <img src={c.hero.imageUrl || '/previews/garment-designer.png'} alt="EVRST Prints Garment Designer" className="w-full block" />
            </div>
          </div>
        </div>
      </section>

      {/* PICK YOUR PATH */}
      <section id="path" className="bg-white">
        <div className="max-w-5xl mx-auto px-6 py-16 md:py-20">
          <div className="text-center mb-10">
            <p className="text-[#B89232] text-[11px] tracking-[0.2em] uppercase font-bold mb-3">{c.path.eyebrow}</p>
            <h2 className="font-heading uppercase font-bold text-4xl md:text-5xl text-ink leading-[0.95]">
              {c.path.titleLine1}
              <span className="italic text-[#B89232] block">{c.path.titleLine2}</span>
            </h2>
            <p className="text-ink-2 mt-4 max-w-xl mx-auto">{c.path.body}</p>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <div className="bg-[#F1E6CB] border border-[#D9C690] rounded-lg p-6">
              <div className="font-heading text-[#B89232] font-bold text-lg mb-1">{c.path.card01.num}</div>
              <h3 className="font-heading uppercase font-bold text-xl text-ink">{c.path.card01.title}</h3>
              <p className="text-ink-2 text-sm mt-2 leading-relaxed">{c.path.card01.body}</p>
              <Link to="/designer" className="btn-gold mt-5 inline-flex">{c.path.card01.ctaLabel}</Link>
            </div>
            <div className="bg-[#F1E6CB] border border-[#D9C690] rounded-lg p-6">
              <div className="font-heading text-[#B89232] font-bold text-lg mb-1">{c.path.card02.num}</div>
              <h3 className="font-heading uppercase font-bold text-xl text-ink">{c.path.card02.title}</h3>
              <p className="text-ink-2 text-sm mt-2 leading-relaxed">{c.path.card02.body}</p>
              <Link to="/dtf" className="btn-gold mt-5 inline-flex">{c.path.card02.ctaLabel}</Link>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS, GARMENTS */}
      <HowItWorksSection block={c.garments} ctaTo="/designer" />

      {/* A LOOK INSIDE, GARMENT DESIGNER */}
      <LookInside block={c.lookInsideGarments} image={c.lookInsideGarments.imageUrl || '/previews/garment-designer.png'} imageAlt="EVRST Prints Garment Designer" />

      {/* HOW IT WORKS, DTF */}
      <HowItWorksSection block={c.dtf} ctaTo="/dtf" />

      {/* A LOOK INSIDE, DTF BUILDER */}
      <LookInside block={c.lookInsideDtf} image={c.lookInsideDtf.imageUrl || '/previews/dtf-builder.png'} imageAlt="EVRST Prints DTF Builder" />

      {/* QUICK ANSWERS / FAQ */}
      <section id="faq" className="bg-surface">
        <div className="max-w-3xl mx-auto px-6 py-16 md:py-20 text-center">
          <p className="text-[#B89232] text-[11px] tracking-[0.2em] uppercase font-bold mb-3">{c.faq.eyebrow}</p>
          <h2 className="font-heading uppercase font-bold text-3xl md:text-4xl text-ink mb-10">{c.faq.title}</h2>
          <div className="text-left space-y-2">
            {c.faq.items.map((f, i) => <FaqRow key={i} q={f.q} a={f.a} />)}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-ink text-white">
        <div className="max-w-3xl mx-auto px-6 py-16 md:py-20 text-center">
          <p className="text-white/55 text-[11px] tracking-[0.2em] uppercase mb-4">{c.finalCta.eyebrow}</p>
          <h2 className="font-heading uppercase font-bold text-4xl md:text-5xl leading-[0.95]">
            {c.finalCta.titleLine1}
            <span className="italic text-gold block">{c.finalCta.titleLine2}</span>
          </h2>
          <p className="text-white/70 mt-4">{c.finalCta.body}</p>
          <div className="mt-7 flex flex-wrap gap-3 justify-center">
            <Link to="/designer" className="btn-gold">{c.finalCta.primaryCtaLabel}</Link>
            <Link to="/dtf" className="inline-flex items-center justify-center gap-2 px-5 py-2.5 border border-white/40 text-white font-semibold uppercase tracking-wide rounded text-sm hover:bg-white hover:text-ink transition-colors">{c.finalCta.secondaryCtaLabel}</Link>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-white border-t border-border">
        <div className="max-w-6xl mx-auto px-6 py-6 flex flex-wrap items-center justify-between gap-4 text-xs text-ink-3">
          <div className="font-heading uppercase font-bold tracking-[0.22em] text-ink">EVRST PRINTS</div>
          <div className="flex gap-5">
            <a href={c.footer.parentUrl} className="hover:text-ink">evrststudio.com</a>
            <a href={`mailto:${c.footer.contactEmail}`} className="hover:text-ink">{c.footer.contactEmail}</a>
          </div>
          <div>© {new Date().getFullYear()} {c.footer.copyrightSuffix}</div>
        </div>
      </footer>
    </div>
  )
}

function HowItWorksSection({ block, ctaTo }: { block: PrintsPageContent['garments']; ctaTo: string }) {
  const id = block.eyebrow.toLowerCase().includes('garment') ? 'garments' : undefined
  return (
    <section id={id} className="bg-ink text-white">
      <div className="max-w-6xl mx-auto px-6 py-16 md:py-20">
        <div className="grid md:grid-cols-[1fr_auto] gap-6 items-end mb-10">
          <div>
            <p className="text-[#B89232] text-[11px] tracking-[0.2em] uppercase font-bold mb-3">{block.eyebrow}</p>
            <h2 className="font-heading uppercase font-bold text-4xl md:text-5xl text-white leading-[0.95]">
              {block.titleLine1}
              <span className="italic text-[#B89232] block">{block.titleLine2}</span>
            </h2>
          </div>
          <p className="text-white/70 text-sm max-w-xs md:text-right">{block.intro}</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {block.steps.map((s, i) => (
            <div key={i} className="bg-white rounded-lg p-5 border border-[#E0D0A0]">
              <div className="text-[#B89232] text-xs uppercase tracking-wider font-bold">{s.num}</div>
              <div className="font-heading uppercase font-bold text-base text-ink mt-1">{s.title}</div>
              <p className="text-ink-2 text-sm mt-2 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
        <div className="mt-8">
          <Link to={ctaTo} className="btn-gold">{block.ctaLabel}</Link>
        </div>
      </div>
    </section>
  )
}

function LookInside({ block, image, imageAlt }: { block: PrintsPageContent['lookInsideGarments']; image: string; imageAlt: string }) {
  return (
    <section className="bg-white">
      <div className="max-w-5xl mx-auto px-6 py-16 md:py-20 text-center">
        <p className="text-[#B89232] text-[11px] tracking-[0.2em] uppercase font-bold mb-3">{block.eyebrow}</p>
        <h2 className="font-heading uppercase font-bold text-3xl md:text-4xl text-ink">{block.title}</h2>
        <p className="text-ink-2 mt-3 max-w-2xl mx-auto">{block.body}</p>
        <div className="mt-8 rounded-lg overflow-hidden border border-border shadow-md">
          <div className="h-7 bg-[#E5E7EB] flex items-center px-3 gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-[#F87171]" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#FBBF24]" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#34D399]" />
          </div>
          <img src={image} alt={imageAlt} className="w-full block" />
        </div>
      </div>
    </section>
  )
}

// Auth box, /api/me-driven so it works regardless of AuthKit React's state.
function HeaderAuthBox() {
  const { user: authkitUser } = useAuth()
  const [me, setMe] = useState<{ role: string } | null>(null)
  const [loaded, setLoaded] = useState(false)
  useEffect(() => {
    let mounted = true
    fetch('/api/me', { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => { if (mounted) setMe(d?.authenticated ? d.user : null) })
      .catch(() => { if (mounted) setMe(null) })
      .finally(() => { if (mounted) setLoaded(true) })
    return () => { mounted = false }
  }, [authkitUser])
  if (!loaded) return null
  const signedIn = !!me || !!authkitUser
  if (!signedIn) return (
    <>
      <a href="/auth/login" className="hover:text-ink">Sign in</a>
      <a href="/auth/login?signup=1" className="text-[#B89232] hover:text-ink font-bold">Sign up</a>
    </>
  )
  const isAdmin = me?.role === 'admin' || me?.role === 'manager'
  return (
    <>
      {isAdmin && <Link to="/admin" className="text-[#B89232] hover:text-ink font-bold">Admin</Link>}
      <Link to="/profile" className="hover:text-ink">Profile</Link>
      <a href="/auth/logout" className="hover:text-ink">Sign out</a>
    </>
  )
}

function FaqRow({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border border-border rounded-lg bg-white overflow-hidden">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between text-left px-5 py-4">
        <span className="font-heading uppercase font-bold text-sm text-ink tracking-wider">{q}</span>
        <span className={'text-[#B89232] text-xl font-bold leading-none transition-transform ' + (open ? 'rotate-45' : '')}>+</span>
      </button>
      {open && <div className="px-5 pb-4 text-ink-2 text-sm leading-relaxed">{a}</div>}
    </div>
  )
}
